import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";

import { aspectRatio as ar, taskTypes } from "../../../../common/parameters";

import enableExperience from "../../../store/plan/actions/enableExperience";
import setExperience from "../../../store/plan/actions/setExperience";
import { post } from "../../../actions/general";
import Draggable from "../../../components/Draggable";
import Tooltip from "../../../components/Tooltip";
import { christmasIcons } from "../../../components/Experience/utils";
import { Radio, Select } from "../../../components/Form";
import Icons from "../../../icons";
import Icon from "../../../icon";
import { christmasPath } from "../../../paths";
import { IRadioEvent, ISelectEvent } from "../../../types";
import { ExperienceOrder, ExperienceTheme } from "../../../../common/types";

import { screenPositions, mainScreenPosition, getContainingScreen, getTaskScores } from "./utils";
import { Screen, Screens, TaskIcons, Positioner, IconScaler, IconScalerDrag, borderWidth, NotSet, Form, Wrapper, Difficulty, IconPulsator, ChristmasIcon } from "./styles";
import { IProps } from "./interfaces";
import messages from "./messages";

export const defaultIconWidth = 72;

export const getScoreValue = (score: string | number | null | undefined) => {
    if (score === undefined) {
        return null;
    }
    return score;
}

const ExperienceEditor = ({ plan, publicView, isSimulation }: IProps) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [originalDragPositions, setOriginalDragPositions] = useState<{ x: number, y: number }[]>([])
    const [dragPositions, setDragPositions] = useState<{ x: number, y: number }[]>([])
    const [currentDrag, setCurrentDrag] = useState<number | null>(null);
    const [screenWidth, setScreenWidth] = useState<number | null>(null);
    const [zIndexes, setZIndexes] = useState<number[]>([]);
    const [hoveredDrag, setHoveredDrag] = useState<number | null>(null);
    const [hoveredDrop, setHoveredDrop] = useState<number | null>(null);
    const [alteredDrag, setAlteredDrag] = useState<null | { id: number, side: "right" | "left"}>(null);
    const handleSetWidth = useCallback(() => {
        if (!plan.experience.enabled) return;
        const width = wrapperRef.current!.getBoundingClientRect().width;
        const newScreenWidth = Math.min(200, Math.floor(width / 3));
        setScreenWidth(newScreenWidth);
        const newScreenHeight = Math.floor((newScreenWidth || 0) * (ar.height / ar.width));
        let iconX = newScreenWidth / 12;
        let iconY = 0;
        const iconWidth = (newScreenWidth || 0) / 3;
        const newPositions = plan.tasks.map(() => {
            const added = newScreenWidth / 3 + newScreenWidth / 6;
            if (iconX + added > newScreenWidth * 3 + newScreenWidth / 12 + 1) {
                iconX = newScreenWidth / 12;
                iconY += iconWidth * 1.4;
            }
            iconX += added;
            return {
                x: iconX - added - borderWidth,
                y: iconY,
            };
        });
        const offset = (newScreenWidth! + iconWidth) / 6;
        const newDragPositions = newPositions.map((position, index) => {
            const taskId = plan.tasks[index].id;
            let taskPosition = plan.experience.positions.indexOf(taskId);
            let positionOffset = 0;
            if (taskPosition === -1) {
                taskPosition = plan.experience.positions.findIndex(position => {
                    if (Array.isArray(position)) {
                        if (position.indexOf(taskId) === 0) {
                            positionOffset = -offset;
                        } else if (position.indexOf(taskId) === 1) {
                            positionOffset = offset;
                        }
                        return position.indexOf(taskId) !== -1
                    }
                    return false;
                })
            }
            if (taskPosition !== -1) {
                return {
                    x: newScreenWidth * screenPositions[taskPosition!].x - screenPositions[taskPosition!].x * borderWidth
                        + (newScreenWidth - iconWidth) / 2 + positionOffset,
                    y: totalLines * iconWidth * 1.4 +
                        newScreenHeight * screenPositions[taskPosition!].y - screenPositions[taskPosition!].y * borderWidth
                        + (newScreenHeight! - iconWidth) / 2,
                }
            }
            return position;
        })
        setZIndexes(plan.tasks.map(() => 1));
        setOriginalDragPositions(newPositions);
        setDragPositions(newDragPositions);
    }, [plan, wrapperRef, setScreenWidth, setZIndexes, setOriginalDragPositions, setDragPositions]);

    useEffect(() => {
        handleSetWidth();
    }, [plan.experience.enabled]);

    useEffect(() => {
        window.addEventListener("resize", handleSetWidth);
        return () => {
            window.removeEventListener("resize", handleSetWidth);
        }
    }, []);
    const screenHeight = screenWidth ? Math.floor((screenWidth || 0) * (ar.height / ar.width)) : 0;
    const iconWidth = (screenWidth || 0) / 3;
    const totalLines = Math.ceil(plan.tasks.length / 6);
    const handleDrag = (index: number, x: number, y: number) => {
        setDragPositions(oldDragPositions => oldDragPositions.map((dragPosition, dragIndex) => {
            if (dragIndex === index) {
                return {
                    x: Math.min(x, screenWidth! * 3 - iconWidth),
                    y: Math.min(y, totalLines * iconWidth * 1.4 + screenHeight * 4 - iconWidth),
                };
            }
            return dragPosition;
        }));
        const { containingScreen, side } = getContainingScreen({ x, y }, screenWidth!, screenHeight, totalLines * iconWidth * 1.4, iconWidth);
        if (side && containingScreen !== null && typeof plan.experience.positions[containingScreen] === "number") {
            const containingTask = plan.tasks.find(task => task.id === plan.experience.positions[containingScreen]);
            const joiningTask = plan.tasks[index];
            if (containingTask?.type === joiningTask.type) {
                setAlteredDrag({
                    id: plan.experience.positions[containingScreen] as number,
                    side,
                });
            } else {
                setAlteredDrag(null);
            }
        } else {
            setAlteredDrag(null);
        }
    };

    const updateZIndexes = (index: number) => {
        setZIndexes(oldZIndexes => {
            const newMaxZIndex = Math.max.apply(null, oldZIndexes);
            return oldZIndexes.map((oldZIndex, dragIndex) => {
                if (index === dragIndex) {
                    return newMaxZIndex + 1;
                }
                return oldZIndex;
            });
        })
    };

    const handleDragStart = (index: number) => {
        setCurrentDrag(index);
        if (plan.experience.positions) {
            const taskId = plan.tasks[index].id;
            const oldDropPosition = plan.experience.positions.indexOf(taskId);
            if (oldDropPosition !== -1) {
                handleSetDragPosition(oldDropPosition, null);
            } else {
                plan.experience.positions.forEach((position, positionIndex) => {
                    const offset = (screenWidth! + iconWidth) / 6;
                    if (Array.isArray(position)) {
                        if (position.indexOf(taskId) === 0) {
                            handleSetDragPosition(positionIndex, position[1]);
                            const taskIndex = plan.tasks.findIndex(task => task.id === position[1]);
                            setDragPositions((oldDragPositions) => {
                                return oldDragPositions.map((oldDragPosition, dragPositionIndex) => {
                                    if (dragPositionIndex === taskIndex) {
                                        return {
                                            ...oldDragPosition,
                                            x: oldDragPosition.x - offset
                                        };
                                    }
                                    return oldDragPosition;
                                });
                            });
                        } else if (position.indexOf(taskId) === 1) {
                            handleSetDragPosition(positionIndex, position[0]);
                            const taskIndex = plan.tasks.findIndex(task => task.id === position[0]);
                            setDragPositions((oldDragPositions) => {
                                return oldDragPositions.map((oldDragPosition, dragPositionIndex) => {
                                    if (dragPositionIndex === taskIndex) {
                                        return {
                                            ...oldDragPosition,
                                            x: oldDragPosition.x + offset
                                        };
                                    }
                                    return oldDragPosition;
                                });
                            });
                        }
                    }
                })
            }
        }
        updateZIndexes(index)
    };

    const handleSetDragPosition = (dropPosition: number, taskId: number | null | number[]) => {
        const experience = {
            ...plan.experience,
        };
        experience.positions[dropPosition] = taskId;
        if (!isSimulation) {
            dispatch(post("experience-positions", { planId: plan._id, positions: experience.positions }));
            setTimeout(() => {
                dispatch(setExperience(experience));
            });
        }
    }

    const handleChangeTheme = ({ target: { value: theme } }: ISelectEvent<ExperienceTheme>) => {
        const experience = {
            ...plan.experience,
        };
        experience.theme = theme;
        if (!isSimulation) {
            dispatch(post("experience-theme", { planId: plan._id, theme }));
            setTimeout(() => {
                dispatch(setExperience(experience));
            });
        }
    }

    const handleDragEnd = () => {
        const { containingScreen } = getContainingScreen(dragPositions[currentDrag!], screenWidth!, screenHeight, totalLines * iconWidth * 1.4, iconWidth);
        let oldDragTaskId: null | number | number[] = null;
        if (containingScreen !== null) {
            oldDragTaskId = plan.experience.positions[containingScreen];
        };
        const offset = (screenWidth! + iconWidth) / 6;
        setDragPositions(oldDragPositions =>
            oldDragPositions.map((dragPosition, index) => {
                let returnDragPosition = dragPosition;
                const taskId = plan.tasks[index].id;
                if (plan.experience.positions && containingScreen !== null) {
                    if (typeof oldDragTaskId === "number" && oldDragTaskId === taskId && !alteredDrag) {
                        returnDragPosition = { ...originalDragPositions[index] };
                    }
                    if (Array.isArray(oldDragTaskId) && (oldDragTaskId[0] === taskId || oldDragTaskId[1] === taskId)) {
                        returnDragPosition = { ...originalDragPositions[index] };
                    }
                }
                if (index === currentDrag!) {
                    if (containingScreen === null) {
                        return originalDragPositions[index];
                    } else {
                        const destinationPosition = {
                            x: screenWidth! * screenPositions[containingScreen].x - screenPositions[containingScreen].x * borderWidth
                                + (screenWidth! - iconWidth) / 2,
                            y: totalLines * iconWidth * 1.4 +
                                screenHeight * screenPositions[containingScreen].y - screenPositions[containingScreen].y * borderWidth
                                + (screenHeight! - iconWidth) / 2,
                        }
                        if (alteredDrag) {
                            if (alteredDrag.side === "left") {
                                handleSetDragPosition(containingScreen, [plan.tasks[index].id, alteredDrag.id]);
                                return {
                                    ...destinationPosition,
                                    x: destinationPosition.x - offset
                                };
                            } else {
                                handleSetDragPosition(containingScreen, [alteredDrag.id, plan.tasks[index].id]);
                                return {
                                    ...destinationPosition,
                                    x: destinationPosition.x + offset
                                };
                            }
                        } else {
                            handleSetDragPosition(containingScreen, plan.tasks[index].id);
                            return destinationPosition;
                        }
                    }
                } else if (alteredDrag && alteredDrag.id === taskId) {
                    if (alteredDrag.side === "left") {
                        return {
                            ...returnDragPosition,
                            x: returnDragPosition.x + offset,
                        }
                    } else {
                        return {
                            ...returnDragPosition,
                            x: returnDragPosition.x - offset,
                        }
                    }
                }
                return returnDragPosition;
            })
        );
       
        setCurrentDrag(null);
        setAlteredDrag(null);
    }

    const handleSetExperience = () => {
        dispatch(enableExperience());
    };

    const handleChangeOrder = ({ target: { value }}: IRadioEvent) => {
        dispatch(setExperience({
            ...plan.experience,
            order: value,
        }))
        dispatch(post("experience-order", { planId: plan._id, order: value }));
    };

    const handleScoreChange = ({ target: { value } }: ISelectEvent<string | number>) => {
        dispatch(setExperience({
            ...plan.experience,
            score: value,
        }))
        dispatch(post("experience-score", { planId: plan._id, score: value === undefined ? null : value }));
    }

    if (!plan.experience.enabled) {
        return (
            <NotSet>
                <h3>
                    <FormattedMessage {...messages.notSetTitle}/>
                </h3>
                <button className="button" onClick={handleSetExperience} disabled={plan.experienceLoading}>
                    <FormattedMessage {...messages.notSetButton}/>
                </button>
            </NotSet>
        );
    }

    return (
        <div>
            {!isSimulation && (
                <p style={{ marginBottom: 24 }}>
                    <FormattedMessage {...messages.info} />
                </p>
            )}
            <Wrapper ref={wrapperRef} style={{
                ...(screenWidth && {
                    maxWidth: screenWidth * 3 - borderWidth * 2 + 276,
                })
            }}>
                {screenWidth &&
                    <>
                        {!isSimulation && (
                            <Form style={{ width: screenWidth! * 3 - borderWidth * 2 }}>
                                <Radio
                                    name="order"
                                    label={<FormattedMessage {...messages.order}/>}
                                    value={plan.experience.order || ExperienceOrder.Fix}
                                    disabled={publicView}
                                    options={[
                                        {
                                            value: ExperienceOrder.Fix,
                                            label: formatMessage(messages.fix)
                                        },
                                        {
                                            value: ExperienceOrder.Free,
                                            label: formatMessage(messages.free)
                                        },
                                    ]}
                                    onChange={handleChangeOrder}
                                />
                                {!plan.experience.theme && (
                                    <Select<string | number | null>
                                        noDelete
                                        showNull
                                        value={getScoreValue(plan.experience.score)}
                                        label={formatMessage(messages.scoreLabel)}
                                        options={[
                                            { value: null, label: formatMessage(messages.coverImage) },
                                            ...(plan.scores || []).map((score, index) => ({
                                                value: score._id,
                                                label: formatMessage(messages.scoreOptionScore, { index: index + 1 }),
                                            })),
                                            ...getTaskScores(plan).map(({ value, label }) => {
                                                return {
                                                    value,
                                                    label: formatMessage(messages.scoreOptionTaskScore, { index: label.index + 1, type: formatMessage({id: `taskTypes.${label.type}` }) }),
                                                }
                                            }),
                                        ]}
                                        name="score"
                                        onChange={handleScoreChange}
                                        disabled={publicView}
                                    />
                                )}
                                <Select
                                    noDelete
                                    showNull
                                    value={plan.experience.theme || null}
                                    label={formatMessage(messages.theme)}
                                    name="theme"
                                    disabled={publicView}
                                    onChange={handleChangeTheme}
                                    options={[
                                        {
                                            value: null,
                                            label: formatMessage(messages.basic)
                                        },
                                        {
                                            value: ExperienceTheme.Easter,
                                            label: formatMessage(messages.easter)
                                        },
                                        {
                                            value: ExperienceTheme.Christmas,
                                            label: formatMessage(messages.christmas)
                                        }
                                    ]}
                                />
                            </Form>
                        )}
                        <div>

                            <TaskIcons style={{ width: screenWidth! * 3 - borderWidth * 2, height: totalLines * iconWidth * 1.4 }}>
                                {plan.tasks.map(({ type }, index) => {
                                    const Icon = Icons[type];
                                    return (
                                        <Positioner
                                            key={index}
                                            style={{
                                                width: iconWidth,
                                                height: iconWidth,
                                                transform: `translate(${originalDragPositions[index].x}px, ${originalDragPositions[index].y}px)`
                                            }}
                                            onMouseEnter={() => {
                                                setHoveredDrop(index);
                                            }}
                                            onMouseLeave={() => {
                                                setHoveredDrop(null);
                                            }}
                                        >
                                            <IconScaler
                                                style={{
                                                    transform: `scale(${iconWidth / defaultIconWidth})`
                                                }}
                                            >
                                                <IconPulsator className={cn({ Pulsating: hoveredDrag === index })}>
                                                    <Icon />
                                                </IconPulsator>
                                            </IconScaler>
                                        </Positioner>
                                    );
                                })}
                                {plan.tasks.map(({ type, id }, index) => {
                                    const TaskIcon = Icons[type];
                                    const position = {
                                        ...dragPositions[index],
                                    };
                                    let difficulty: null | "Hard" | "Easy" = null;
                                    if (alteredDrag && alteredDrag.id === id) {
                                        const offset = (screenWidth + iconWidth) / 6;
                                        if (alteredDrag.side === "left") {
                                            position.x += offset;
                                            difficulty = "Hard";
                                        } else {
                                            position.x -= offset;
                                            difficulty = "Easy";
                                        }
                                    } else if (alteredDrag && currentDrag === index) {
                                        difficulty = alteredDrag.side === "left" ? "Easy" : "Hard";
                                    } else {
                                        plan.experience.positions.forEach(position => {
                                            if (Array.isArray(position)) {
                                                if (position[0] === id) {
                                                    difficulty = "Easy";
                                                } else if (position[1] === id) {
                                                    difficulty = "Hard";
                                                }
                                            }
                                        })
                                    }
                                    return (
                                        <Draggable
                                            key={index}
                                            position={position}
                                            onDrag={(x: number, y: number) => handleDrag(index, x, y)}
                                            onDragStart={() => handleDragStart(index)}
                                            onStop={handleDragEnd}
                                            zIndex={zIndexes[index]}
                                            transition={currentDrag !== index}
                                            disabled={publicView}
                                            onMouseEnter={() => updateZIndexes(index)}
                                        >
                                            <Tooltip label={taskTypes(formatMessage)[type]} marginTop={screenWidth / 16}>
                                                <IconScalerDrag
                                                    style={{
                                                        width: iconWidth,
                                                        height: iconWidth,
                                                        transform: `scale(${iconWidth / defaultIconWidth})`
                                                    }}
                                                    onMouseEnter={() => {
                                                        setHoveredDrag(index);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredDrag(null);
                                                    }}
                                                >
                                                    <IconPulsator className={cn({ Pulsating: hoveredDrop === index })}>
                                                        <TaskIcon active />
                                                        {difficulty && (
                                                            <Difficulty className={difficulty}>
                                                                <Icon i="chevron-right" />
                                                            </Difficulty>
                                                        )}
                                                    </IconPulsator>
                                                </IconScalerDrag>
                                            </Tooltip>
                                        </Draggable>
                                    );
                                })}
                            </TaskIcons>
                            <Screens style={{
                                height: screenHeight * 4,
                                width: screenWidth! * 3 - borderWidth * 2,
                            }}>
                                {screenPositions.map(({ x, y }, index) => {
                                    return (
                                        <Screen
                                            key={index}
                                            className={cn({ Active: typeof plan.experience.positions[index] === "number" || Array.isArray(plan.experience.positions[index]) })}
                                            style={{
                                                width: screenWidth!,
                                                height: screenHeight,
                                                left: x * screenWidth! - x * borderWidth,
                                                top: y * screenHeight - y * borderWidth,
                                            }}
                                        >
                                            {plan.experience?.theme === ExperienceTheme.Christmas && (
                                                <ChristmasIcon
                                                    src={`${christmasPath}/images/experience/${christmasIcons[index]}.svg`}
                                                />
                                            )}
                                        </Screen>
                                    )
                                })}
                                <Screen
                                    className="StartScreen"
                                    style={{
                                        width: screenWidth!,
                                        height: screenHeight,
                                        top: mainScreenPosition.y * screenHeight - mainScreenPosition.y * borderWidth,
                                        left: mainScreenPosition.x * screenWidth! - mainScreenPosition.x * borderWidth,
                                    }}
                                />
                            </Screens>
                        </div>
                    </>
                }
            </Wrapper>
        </div>
    )
};

export default ExperienceEditor;
